import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useHomeCta } from "../hooks/useHomeCta";
import { openAgent } from "../utils/openAgent";


const EXPLORE_INSIGHTS = [
  {
    id: "small-business-lender",
    cat: "FINANCIAL SERVICES",
    title: "A small-business lender cuts loan decisions from six days to five hours without loosening its credit standards",
    sub: "How A leading Finance Company used AI to prepare the loan file, and kept its credit officers in charge of the decision.",
    img: "/images/insights/explore-insights-1.jpg",
  },
  {
    id: "end-to-end-operations",
    cat: "COGNITIVE ENTERPRISE",
    title: "AI Transforms End-to-End Business Operations for Modern Enterprises",
    sub: "How AI integrates data, people and processes to automate workflows, improve decisions and deliver faster, smarter business outcomes.",
    img: "/images/insights/explore-insights-2.jpg",
  },
  {
    id: "real-business-economics",
    cat: "AI ECONOMICS",
    title: "Understanding the Real Business Economics of Artificial Intelligence Investments",
    sub: "How organizations measure AI's impact on costs, productivity, and revenue to maximize returns and build more resilient, competitive businesses.",
    img: "/images/insights/explore-insights-3.jpg",
  },
];

/* Custom Arrow Navigation Button matching exact Figma specs in Screenshot 1 & 5:
   Width: 56px, Height: 56px, Radius: rounded-full (56px)
   Border: 2px brand gradient (rgba(5, 214, 218, 1) to rgba(49, 112, 247, 1))
   Background: solid white (rgba(255, 255, 255, 1))
   Drop shadow: 4px 4px 10px rgba(0, 0, 0, 0.18)
   Arrow icon: clean sharp black SVG arrow (stroke 2.5)
*/
const ArrowNavButton = ({ direction, onClick }) => (
  <button
    type="button"
    onClick={(e) => {
      e.stopPropagation();
      onClick();
    }}
    aria-label={direction === "left" ? "Previous insight" : "Next insight"}
    className="group relative flex h-[56px] w-[56px] items-center justify-center rounded-full p-[2px] transition-all duration-300 hover:scale-105 active:scale-95 cursor-pointer z-30"
    style={{
      background:
        "linear-gradient(135deg, rgba(5, 214, 218, 1) 0%, rgba(49, 112, 247, 1) 100%)",
      boxShadow: "4px 4px 10px rgba(0, 0, 0, 0.18)",
    }}
  >
    <div className="flex h-full w-full items-center justify-center rounded-full bg-white transition-colors">
      <svg
        className={`h-5 w-5 text-black transition-transform duration-200 ${
          direction === "left"
            ? "group-hover:-translate-x-0.5"
            : "group-hover:translate-x-0.5"
        }`}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {direction === "left" ? (
          <>
            <line x1="19" y1="12" x2="5" y2="12" />
            <polyline points="12 19 5 12 12 5" />
          </>
        ) : (
          <>
            <line x1="5" y1="12" x2="19" y2="12" />
            <polyline points="12 5 19 12 12 19" />
          </>
        )}
      </svg>
    </div>
  </button>
);

const Insights = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [currentIndex, setCurrentIndex] = useState(0);
  const goToHomeCta = useHomeCta();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handlePrev = () => {
    setCurrentIndex(
      (prev) => (prev - 1 + EXPLORE_INSIGHTS.length) % EXPLORE_INSIGHTS.length
    );
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % EXPLORE_INSIGHTS.length);
  };

  const visibleInsights = [
    EXPLORE_INSIGHTS[currentIndex % EXPLORE_INSIGHTS.length],
    EXPLORE_INSIGHTS[(currentIndex + 1) % EXPLORE_INSIGHTS.length],
    EXPLORE_INSIGHTS[(currentIndex + 2) % EXPLORE_INSIGHTS.length],
  ];

  return (
    <div className="min-h-screen w-full bg-white">
      <Navbar />
      <main>
        {/* Fullscreen Hero */}
        <section className="relative flex min-h-[100dvh] w-full items-center overflow-hidden" style={{ background: "#010710" }}>
          <div className="pointer-events-none absolute" style={{ width: 1187, height: 345, left: -238, top: -89, background: "#173862", filter: "blur(92.65px)" }} />
          <div className="relative mx-auto grid w-full max-w-[1440px] grid-cols-1 items-center gap-10 px-6 py-24 lg:grid-cols-2 lg:px-[70px]">
            <div className="flex max-w-[556px] flex-col gap-6">
              <p className="text-[11px] font-bold uppercase tracking-[2.2px] text-white/80">INSIGHTS</p>
              <h1 className="text-[48px] font-bold leading-[1.1] text-white sm:text-[60px] lg:text-[64px]">
                Thinking about <span style={{ color: "#05D6DA" }}>what comes next.</span>
              </h1>
              <p className="text-[17px] leading-[28px] text-white/80">
                Ideas, perspectives and practical insights on how AI is transforming business — from strategy and economics to technology and real-world execution.
              </p>
              <div className="mt-2">
                <button 
                  onClick={() => document.querySelector("#content")?.scrollIntoView({ behavior: "smooth" })} 
                  className="flex w-fit items-center gap-2 rounded-full bg-white px-7 py-[15px] text-[15px] font-medium text-[#0F172A] transition-transform hover:scale-[1.03]" 
                  style={{ boxShadow: "0px 4px 24px rgba(47,115,246,0.73)" }}
                >
                  Explore All Insights <ArrowRight className="h-4 w-4" strokeWidth={2.5} />
                </button>
              </div>
            </div>
            <div className="flex justify-center lg:justify-end">
              <img src="/images/insights/hero.png" alt="Insights" className="w-full max-w-[650px] object-contain" />
            </div>
          </div>
        </section>

        {/* Explore Our Insights Section - exact match to user Screenshot 1, 2, 3 */}
        <section
          id="content"
          className="w-full py-16 sm:py-20 lg:py-24"
          style={{ backgroundColor: "rgba(245, 250, 254, 1)" }}
        >
          <div className="mx-auto max-w-[1440px] px-6 lg:px-[70px]">
            {/* Header matching Screenshot 1 */}
            <div className="mx-auto max-w-[800px] text-center mb-12 sm:mb-16">
              <p className="text-[11px] sm:text-[12px] font-bold uppercase tracking-[2.5px] text-[#94A3B8]">
                LATEST INSIGHTS
              </p>
              <h2 className="mt-3 text-[38px] sm:text-[46px] lg:text-[52px] font-bold tracking-tight text-[#0F172A] leading-[1.12]">
                Explore Our <span className="text-[#2F6DF6]">Insights</span>
              </h2>
            </div>

            {/* 3 Insight Cards with embedded Left & Right Arrow Navigation matching Screenshot 1, 2, 3 */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7 lg:gap-8">
              {visibleInsights.map((item, idx) => (
                <div
                  key={`${item.id}-${idx}`}
                  onClick={() => {
                    if (
                      item.id === "small-business-lender" ||
                      item.cat === "FINANCIAL SERVICES"
                    ) {
                      navigate("/insights/financial-services");
                      window.scrollTo(0, 0);
                    } else {
                      toast.info(item.title);
                    }
                  }}
                  className="group relative flex flex-col justify-between overflow-hidden rounded-[27px] border border-[#E4E4E4] bg-white p-3.5 sm:p-4 transition-all duration-300 hover:shadow-[4px_4px_9.7px_rgba(0,0,0,0.13)] cursor-pointer"
                >
                  <div>
                    {/* Card Image - Static without zoom on hover */}
                    <div className="relative h-[210px] sm:h-[230px] lg:h-[245px] w-full overflow-hidden rounded-[20px] bg-[#F1F5F9]">
                      <img
                        src={item.img}
                        alt={item.title}
                        className="h-full w-full object-cover select-none"
                      />

                      {/* Left Nav Arrow Button on Card 1 (Bottom Left) matching Screenshot 1 */}
                      {idx === 0 && (
                        <div className="absolute bottom-3.5 left-3.5 z-30">
                          <ArrowNavButton direction="left" onClick={handlePrev} />
                        </div>
                      )}

                      {/* Right Nav Arrow Button on Card 3 (Bottom Right) matching Screenshot 1 */}
                      {idx === 2 && (
                        <div className="absolute bottom-3.5 right-3.5 z-30">
                          <ArrowNavButton direction="right" onClick={handleNext} />
                        </div>
                      )}
                    </div>

                    {/* Card Content */}
                    <div className="flex flex-col gap-2.5 px-1 py-4 sm:px-1.5 sm:py-5">
                      <span className="text-[11px] font-bold uppercase tracking-[1.5px] text-[#8E9CAB]">
                        {item.cat}
                      </span>

                      <h3 className="text-[17px] sm:text-[18px] lg:text-[19px] font-bold leading-[25px] sm:leading-[26px] text-[#0F172A]">
                        <span className="transition-all duration-200 group-hover:bg-gradient-to-r group-hover:from-[#00D2EE] group-hover:to-[#2B7FFF] group-hover:bg-clip-text group-hover:text-transparent">
                          {item.title}
                        </span>
                        <span className="inline-block ml-1.5 align-middle text-[#2F6DF6] transition-transform duration-200 group-hover:translate-x-1">
                          <svg
                            className="inline h-3.5 w-3.5"
                            viewBox="0 0 12 12"
                            fill="currentColor"
                          >
                            <path d="M3 1.5l6 4.5-6 4.5V1.5z" />
                          </svg>
                        </span>
                      </h3>

                      <p className="mt-1 text-[13.5px] leading-[22px] text-[#64748B]">
                        {item.sub}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Mobile / Tablet Navigation buttons */}
            <div className="mt-8 flex items-center justify-center gap-4 lg:hidden">
              <ArrowNavButton direction="left" onClick={handlePrev} />
              <ArrowNavButton direction="right" onClick={handleNext} />
            </div>
          </div>
        </section>

        {/* Newsletter + resource */}
        <section className="w-full bg-white">
          <div className="mx-auto grid max-w-[1440px] grid-cols-1 gap-6 px-6 py-16 lg:grid-cols-2 lg:px-[70px]">
            <div className="rounded-[20px] border border-[#E2E8F0] bg-[#F8FAFC] p-8 lg:p-12">
              <h3 className="text-[26px] font-bold text-[#0F172A]">Get insights in your inbox.</h3>
              <p className="mt-2 text-[15px] text-[#64748B]">Practical ideas on AI and the future of business.</p>
              <form onSubmit={(e) => { e.preventDefault(); if (!email.trim()) return toast("Enter your email"); toast.success("Subscribed!"); setEmail(""); }} className="mt-6 flex flex-col gap-3 sm:flex-row">
                <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Enter your email address" className="flex-1 rounded-[10px] border border-[#CBD5E1] bg-white px-5 py-3.5 text-[14px] focus:outline-none focus:ring-2 focus:ring-[#3B82F6] focus:border-transparent" />
                <button type="submit" className="rounded-[10px] bg-[#0F172A] px-7 py-3.5 text-[14px] font-semibold text-white transition-colors hover:bg-[#1E293B]">Subscribe</button>
              </form>
            </div>
            
            <div className="flex flex-col justify-center rounded-[20px] p-8 text-white lg:p-12" style={{ background: "linear-gradient(135deg, #06101B 0%, #0B1A2A 100%)" }}>
              <p className="text-[11px] font-bold uppercase tracking-[2px] text-[#94A3B8]">FEATURED RESOURCE</p>
              <h3 className="mt-3 text-[26px] font-bold">AI Opportunity Index</h3>
              <p className="mt-2 text-[15px] text-white/70">A data-driven analysis of where the biggest AI opportunities lie across industries.</p>
              <button onClick={() => toast("Downloading report")} className="mt-6 flex w-fit items-center gap-2 rounded-full bg-white px-6 py-3 text-[14px] font-semibold text-black transition-transform hover:scale-[1.03]">
                Download Report <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section id="cta" className="relative w-full overflow-hidden" style={{ minHeight: 320 }}>
          <img src="/images/insights/cta.png" alt="" aria-hidden className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, #04101D 0%, rgba(7,22,38,0.35) 55%, rgba(8,42,89,0) 100%)" }} />
          <div className="relative mx-auto max-w-[1440px] px-6 py-20 lg:px-[75px]">
            <div className="flex max-w-[560px] flex-col gap-4">
              <p className="text-[12px] font-semibold uppercase tracking-[3.36px] text-[#F8FBFF]">A MORE INTELLIGENT TOMORROW</p>
              <h2 className="text-[28px] font-semibold text-[#F8FBFF] sm:text-[32px]">Turn insight into impact.</h2>
              <p className="text-[14px] text-[#C9D3DF]">Explore how Simpel AI can help you discover, build and scale AI opportunities for your business.</p>
              <button onClick={openAgent} className="flex w-fit items-center gap-2 rounded-full bg-white px-6 py-3 text-[13px] font-medium text-black transition-transform hover:scale-[1.03]" style={{ boxShadow: "0px 4px 21px rgba(47,115,246,0.73)" }}>
                Discover Your AI Opportunities <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Insights;
